import { useMemo } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  BeakerIcon,
  CloudIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
);

const gridColor = 'rgba(255, 255, 255, 0.08)';
const tickColor = 'rgba(199, 210, 254, 0.85)';

function chartOptions({ yMin, yMax, yStep, ySuffix = '', isMobile = false }) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#fff',
        bodyColor: '#cbd5e1',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 10,
      },
    },
    scales: {
      x: {
        ticks: {
          color: tickColor,
          maxRotation: isMobile ? 45 : 0,
          autoSkip: true,
          maxTicksLimit: isMobile ? 6 : 12,
          font: { size: isMobile ? 9 : 11 },
        },
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        border: { display: false },
      },
      y: {
        min: yMin,
        max: yMax,
        ticks: {
          color: tickColor,
          stepSize: yStep,
          callback: (v) => `${v}${ySuffix}`,
          font: { size: isMobile ? 9 : 11 },
        },
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        border: { display: false },
      },
    },
  };
}

function niceScale(values, padding = 4) {
  const nums = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (!nums.length) return { min: 0, max: 100, step: 10 };
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const lo = Math.floor(min - padding);
  const hi = Math.ceil(max + padding);
  const span = hi - lo || 10;
  const step = Math.max(1, Math.ceil(span / 5));
  return { min: lo, max: hi, step };
}

function GraphCard({ icon: Icon, title, children, iconClassName }) {
  return (
    <div className="glass rounded-2xl p-4 sm:p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon className={`h-5 w-5 shrink-0 ${iconClassName}`} />
        <h3 className="text-sm font-semibold text-white sm:text-base">{title}</h3>
      </div>
      <div className="h-[180px] w-full sm:h-[220px] lg:h-[260px]">{children}</div>
    </div>
  );
}

export default function ForecastCharts({ points, loading }) {
  const isMobile = useMediaQuery('(max-width: 639px)');
  const labels = useMemo(() => points.map((p) => p.label), [points]);
  const temps = useMemo(
    () => points.map((p) => (p.tempF != null ? p.tempF : null)),
    [points]
  );
  const pops = useMemo(() => points.map((p) => p.popPercent), [points]);
  const winds = useMemo(() => points.map((p) => p.windMph), [points]);

  const tempScale = niceScale(temps.filter((t) => t != null), 3);
  const popScale = { min: 0, max: 100, step: 25 };
  const windScale = niceScale(winds, 2);

  const tempData = {
    labels,
    datasets: [
      {
        data: temps,
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.35)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        fill: true,
        tension: 0.35,
      },
    ],
  };

  const popData = {
    labels,
    datasets: [
      {
        data: pops,
        backgroundColor: 'rgba(56, 189, 248, 0.85)',
        borderRadius: 3,
        barPercentage: 0.85,
      },
    ],
  };

  const windData = {
    labels,
    datasets: [
      {
        data: winds,
        borderColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return '#38bdf8';
          const g = c.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
          g.addColorStop(0, '#38bdf8');
          g.addColorStop(1, '#a855f7');
          return g;
        },
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        pointRadius: 4,
        pointBackgroundColor: '#38bdf8',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        tension: 0.35,
      },
    ],
  };

  if (loading && !points.length) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="glass h-[240px] animate-pulse rounded-2xl bg-white/5 sm:h-[280px] lg:h-[320px]"
          />
        ))}
      </div>
    );
  }

  if (!points.length) return null;

  return (
    <div className="space-y-4">
      <GraphCard
        icon={BeakerIcon}
        title="Temperature (°F)"
        iconClassName="text-orange-400"
      >
        <Line
          data={tempData}
          options={chartOptions({
            yMin: tempScale.min,
            yMax: tempScale.max,
            yStep: tempScale.step,
            isMobile,
          })}
        />
      </GraphCard>

      <GraphCard
        icon={CloudIcon}
        title="Precipitation Probability (%)"
        iconClassName="text-sky-400"
      >
        <Bar
          data={popData}
          options={chartOptions({
            yMin: popScale.min,
            yMax: popScale.max,
            yStep: popScale.step,
            isMobile,
          })}
        />
      </GraphCard>

      <GraphCard
        icon={ArrowTrendingUpIcon}
        title="Wind Speed (mph)"
        iconClassName="text-sky-400"
      >
        <Line
          data={windData}
          options={chartOptions({
            yMin: windScale.min,
            yMax: windScale.max,
            yStep: windScale.step,
            isMobile,
          })}
        />
      </GraphCard>
    </div>
  );
}
